import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { 
  Heart, ArrowLeft, BookOpen, Clock, Share2, 
  ChevronRight, Sparkles, Play, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

// Article content database
const ARTICLES = {
  'what-is-gestalt-language-processing': {
    title: 'What is Gestalt Language Processing?',
    category: 'GLP Basics',
    readTime: '5 min read',
    author: 'PlayFirst Team',
    publishDate: 'December 2025',
    heroColor: 'from-[#7c6f9c] to-[#5d5478]',
    content: [
      {
        type: 'intro',
        text: 'Gestalt Language Processing (GLP) is a natural way that many children acquire language — by learning whole phrases or "chunks" of language before breaking them down into smaller parts. Understanding GLP is essential for providing affirming, effective speech therapy to gestalt language processors.'
      },
      {
        type: 'heading',
        text: 'What Makes a Gestalt Language Processor?'
      },
      {
        type: 'paragraph',
        text: 'Unlike analytical language processors who build language word-by-word, gestalt processors start with rich, intonation-laden chunks of language they\'ve heard — often from movies, songs, books, or caregivers. These "gestalts" carry emotional meaning and are used communicatively, even if they don\'t yet sound like typical sentences.'
      },
      {
        type: 'callout',
        title: 'Key Insight',
        text: 'Echolalia isn\'t meaningless repetition — it\'s the first stage of language development for gestalt processors. These echoed phrases are attempts at communication and should be honored, not discouraged.'
      },
      {
        type: 'heading',
        text: 'The Natural Language Acquisition (NLA) Stages'
      },
      {
        type: 'paragraph',
        text: 'Dr. Marge Blanc\'s Natural Language Acquisition framework describes how gestalt processors develop language through 6 stages:'
      },
      {
        type: 'list',
        items: [
          { stage: 'Stage 1', title: 'Echolalia', desc: 'Using whole gestalts (delayed echoes) with communicative intent' },
          { stage: 'Stage 2', title: 'Mitigating', desc: 'Breaking gestalts into smaller chunks, mixing and matching pieces' },
          { stage: 'Stage 3', title: 'Single Words + Chunks', desc: 'Isolating single words while still using some gestalt chunks' },
          { stage: 'Stage 4', title: 'Novel Phrases', desc: 'Combining single words into original two-word combinations' },
          { stage: 'Stage 5', title: 'Grammar Emerging', desc: 'Adding grammar markers, pronouns, and word endings' },
          { stage: 'Stage 6', title: 'Complex Language', desc: 'Using advanced grammar and complex sentence structures' }
        ]
      },
      {
        type: 'heading',
        text: 'How is GLP Different from Analytical Processing?'
      },
      {
        type: 'comparison',
        analytical: [
          'Starts with single words',
          'Builds up to phrases',
          'Less reliance on intonation',
          'Earlier pronoun use',
          'More typical developmental sequence'
        ],
        gestalt: [
          'Starts with whole phrases',
          'Breaks down into parts',
          'Rich intonation patterns',
          'Later pronoun development',
          'Different but equally valid path'
        ]
      },
      {
        type: 'heading',
        text: 'Supporting Gestalt Language Processors'
      },
      {
        type: 'paragraph',
        text: 'The most important shift in supporting GLPs is moving from trying to "fix" echolalia to understanding and building on it. This means:'
      },
      {
        type: 'tips',
        items: [
          'Acknowledge and respond to gestalt communication attempts',
          'Model language at the child\'s current NLA stage (or just above)',
          'Use the child\'s interests to provide meaningful language models',
          'Focus on connection and communication over "correct" grammar',
          'Document gestalts and their communicative meanings'
        ]
      },
      {
        type: 'callout',
        title: 'Remember',
        text: 'There is no "wrong" way to develop language. Gestalt processing is a natural, valid path to communication. Our job as therapists is to meet children where they are and support their unique journey.'
      }
    ],
    relatedArticles: ['neurodiversity-affirming-reports', 'play-based-glp-strategies']
  },
  'neurodiversity-affirming-reports': {
    title: 'Writing Neurodiversity-Affirming Reports',
    category: 'Clinical Writing',
    readTime: '7 min read',
    author: 'PlayFirst Team',
    publishDate: 'December 2025',
    heroColor: 'from-[#9c8b7c] to-[#7a6b5b]',
    content: [
      {
        type: 'intro',
        text: 'The language we use in clinical documentation matters. Neurodiversity-affirming reports focus on strengths, honor different ways of communicating, and avoid pathologizing natural neurological differences. Here\'s how to shift your clinical writing.'
      },
      {
        type: 'heading',
        text: 'Why Language Matters'
      },
      {
        type: 'paragraph',
        text: 'Clinical reports are read by parents, educators, insurance companies, and sometimes the clients themselves years later. The words we choose can either empower or harm. Deficit-based language reinforces the idea that neurodivergent individuals are "broken" and need to be "fixed."'
      },
      {
        type: 'callout',
        title: 'The Shift',
        text: 'Instead of focusing on what a child CAN\'T do, affirming reports highlight what they CAN do, how they communicate, and what supports help them thrive.'
      },
      {
        type: 'heading',
        text: 'Language Swaps You Can Use Today'
      },
      {
        type: 'swaps',
        items: [
          { before: 'Deficits in social communication', after: 'Differences in social communication style' },
          { before: 'Failed to make eye contact', after: 'Demonstrated alternative listening behaviors' },
          { before: 'Exhibits echolalia', after: 'Uses gestalt language processing; communicates through meaningful echoed phrases' },
          { before: 'Non-verbal', after: 'Uses non-speaking communication methods' },
          { before: 'Low functioning', after: 'Has high support needs' },
          { before: 'Behavior problems', after: 'Communicating unmet needs through behavior' },
          { before: 'Lacks joint attention', after: 'Shows joint attention through parallel play and shared interests' },
          { before: 'Rigid/inflexible', after: 'Finds comfort in predictability and routine' },
          { before: 'Obsessive interests', after: 'Deep, passionate interests (which can be leveraged for therapy!)' },
          { before: 'Symptoms of autism', after: 'Autistic characteristics' }
        ]
      },
      {
        type: 'heading',
        text: 'Structuring Affirming Reports'
      },
      {
        type: 'paragraph',
        text: 'Beyond word choice, the structure of your report can reinforce an affirming approach:'
      },
      {
        type: 'tips',
        items: [
          'Start with strengths and what the child enjoys',
          'Describe communication differences, not deficits',
          'Include the child\'s perspective when possible',
          'Frame goals around participation and quality of life, not "normalcy"',
          'Acknowledge that the environment may need to adapt, not just the child',
          'Use identity-first language if preferred by the family (e.g., "autistic child" vs "child with autism")'
        ]
      },
      {
        type: 'heading',
        text: 'Before & After Example'
      },
      {
        type: 'beforeafter',
        before: 'Patient presents with severe deficits in expressive language. She failed to respond to her name and exhibited repetitive echolalia throughout the session. Patient lacks functional communication skills and demonstrates poor eye contact.',
        after: 'Emma is a joyful 4-year-old who communicates through gestalt language processing, currently at NLA Stage 2. She uses rich, meaningful echoed phrases from her favorite shows to express her needs and connect with others. Emma demonstrates strong auditory memory and shows engagement through parallel play and shared excitement about her interests. She responds best when given processing time and when communication partners acknowledge her gestalt attempts.'
      },
      {
        type: 'callout',
        title: 'Remember',
        text: 'You\'re not just writing a report — you\'re shaping how people see and treat this child. Choose words that honor their humanity and unique way of being in the world.'
      }
    ],
    relatedArticles: ['what-is-gestalt-language-processing', 'play-based-glp-strategies']
  },
  'play-based-glp-strategies': {
    title: 'Play-Based Strategies for GLP Kids',
    category: 'Intervention',
    readTime: '6 min read',
    author: 'PlayFirst Team',
    publishDate: 'December 2025',
    heroColor: 'from-[#7c9c8b] to-[#5b7a6b]',
    content: [
      {
        type: 'intro',
        text: 'Play is the foundation of language learning — especially for gestalt language processors. By following a child\'s lead and embedding language models into their natural interests, we can support authentic communication development. Here are practical strategies for each NLA stage.'
      },
      {
        type: 'heading',
        text: 'Why Play-Based Intervention Works'
      },
      {
        type: 'paragraph',
        text: 'Gestalt processors learn language that is emotionally meaningful and connected to real experiences. Drill-based therapy that focuses on isolated words often doesn\'t stick because it lacks the emotional context that makes language memorable for these learners.'
      },
      {
        type: 'callout',
        title: 'The Magic Formula',
        text: 'Special Interest + Emotional Connection + Language Model = Meaningful Language Learning'
      },
      {
        type: 'heading',
        text: 'Strategies by NLA Stage'
      },
      {
        type: 'stagestrategy',
        stage: 'Stage 1: Echolalia',
        goal: 'Acknowledge gestalts, model new ones',
        strategies: [
          'Join their play without trying to change it',
          'Use the same intonation patterns they love',
          'Model short, emotionally-rich phrases during high-interest moments',
          'Respond to their gestalts as real communication',
          'Songs and scripts from their favorite media are your friends!'
        ],
        example: 'If a child loves water play and quotes "Let it go!" from Frozen, you might model "Splash it down!" or "Water goes whoooosh!" with similar excitement and intonation.'
      },
      {
        type: 'stagestrategy',
        stage: 'Stage 2: Mitigating',
        goal: 'Support breaking gestalts into chunks',
        strategies: [
          'Model partial phrases from their existing gestalts',
          'Create opportunities for mix-and-match language',
          'Use carrier phrases with swappable endings',
          'Follow their lead on which chunks feel right',
          'Celebrate new combinations!'
        ],
        example: 'If a child uses "I want juice box" as a full gestalt, you might model "I want... crackers!" or "I want... bubbles!" to show the swappable pattern.'
      },
      {
        type: 'stagestrategy',
        stage: 'Stage 3: Single Words + Chunks',
        goal: 'Support isolation of single words',
        strategies: [
          'Narrate play with single words that match their actions',
          'Emphasize key words within natural phrases',
          'Use pointing and visual supports to highlight single words',
          'Pause expectantly to invite single-word responses',
          'Model single words for high-desire items'
        ],
        example: 'During bubbles play: "Pop! Pop! More? More bubbles! Big! Big bubble! Pop!"'
      },
      {
        type: 'stagestrategy',
        stage: 'Stage 4+: Novel Combinations',
        goal: 'Support original phrase building',
        strategies: [
          'Model two-word combinations during play',
          'Create silly combinations to show flexibility',
          'Ask open-ended questions that invite combinations',
          'Expand their utterances by one element',
          'Use visual supports for sentence building if helpful'
        ],
        example: 'Child says "car." You might model "fast car!" or "car go!" then ask "What does car do?" during racing play.'
      },
      {
        type: 'heading',
        text: 'Using Special Interests'
      },
      {
        type: 'paragraph',
        text: 'Special interests aren\'t distractions — they\'re FUEL for language learning. A child who loves dinosaurs will learn more language through dinosaur play than through any "age-appropriate" activity they don\'t care about.'
      },
      {
        type: 'tips',
        items: [
          'Ask caregivers about current obsessions and favorite media',
          'Use characters and scripts from their interests as language models',
          'Create therapy materials around their passions',
          'Let them lead — you follow and model',
          'Rotate activities within their interest (dinosaurs can swim, eat, sleep, fight, etc.)'
        ]
      },
      {
        type: 'heading',
        text: 'Sample Activity Ideas'
      },
      {
        type: 'activities',
        items: [
          { interest: 'Water Play', activities: 'Pouring, splashing, boat racing, washing toys, ice melting, bubble baths for figurines' },
          { interest: 'Vehicles', activities: 'Ramps and crashes, car wash, traffic jams, races, construction sites, rescue missions' },
          { interest: 'Music', activities: 'Instrument exploration, song fill-ins, movement games, making up silly songs, musical scripts' },
          { interest: 'Animals', activities: 'Habitat creation, feeding routines, animal families, vet play, safari adventures' },
          { interest: 'Sensory', activities: 'Playdough, slime, sand, kinetic sand, shaving cream, bean bins with hidden treasures' }
        ]
      },
      {
        type: 'callout',
        title: 'Final Thought',
        text: 'The best therapy doesn\'t feel like therapy. When a child is genuinely engaged in play they love, language learning happens naturally. Trust the process, follow their lead, and enjoy the connection.'
      }
    ],
    relatedArticles: ['what-is-gestalt-language-processing', 'neurodiversity-affirming-reports']
  }
};

const ARTICLE_TITLES = {
  'what-is-gestalt-language-processing': 'What is Gestalt Language Processing?',
  'neurodiversity-affirming-reports': 'Writing Neurodiversity-Affirming Reports',
  'play-based-glp-strategies': 'Play-Based Strategies for GLP Kids'
};

export default function ResourceArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = ARTICLES[slug];

  if (!article) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <h1 className="text-2xl font-serif text-primary-900 mb-4">Article Not Found</h1>
          <p className="text-stone-600 mb-6">Sorry, we couldn't find that article.</p>
          <Button onClick={() => navigate('/')} className="rounded-full">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
          </Button>
        </Card>
      </div>
    );
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: article.title,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const renderContent = (block, idx) => {
    switch (block.type) {
      case 'intro':
        return (
          <p key={idx} className="text-lg text-stone-700 leading-relaxed mb-8 font-medium">
            {block.text}
          </p>
        );
      case 'heading':
        return (
          <h2 key={idx} className="text-2xl font-serif text-primary-900 mt-10 mb-4">
            {block.text}
          </h2>
        );
      case 'paragraph':
        return (
          <p key={idx} className="text-stone-600 leading-relaxed mb-4">
            {block.text}
          </p>
        );
      case 'callout':
        return (
          <Card key={idx} className="bg-[#f5f3f8] border-[#e0dce8] p-6 my-6 rounded-xl">
            <p className="font-medium text-[#7c6f9c] mb-2">{block.title}</p>
            <p className="text-stone-700">{block.text}</p>
          </Card>
        );
      case 'list':
        return (
          <div key={idx} className="space-y-3 my-6">
            {block.items.map((item, i) => (
              <div key={i} className="flex gap-4 p-4 bg-stone-50 rounded-xl">
                <Badge className="bg-[#7c6f9c] text-white shrink-0">{item.stage}</Badge>
                <div>
                  <p className="font-medium text-primary-900">{item.title}</p>
                  <p className="text-sm text-stone-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        );
      case 'comparison':
        return (
          <div key={idx} className="grid md:grid-cols-2 gap-4 my-6">
            <Card className="p-5 rounded-xl border-stone-200">
              <p className="font-medium text-primary-900 mb-3">Analytical Processors</p>
              <ul className="space-y-2">
                {block.analytical.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                    <ChevronRight className="w-4 h-4 text-stone-400 shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </Card>
            <Card className="p-5 rounded-xl border-[#7c6f9c] bg-[#f5f3f8]">
              <p className="font-medium text-[#7c6f9c] mb-3">Gestalt Processors</p>
              <ul className="space-y-2">
                {block.gestalt.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-stone-700">
                    <Sparkles className="w-4 h-4 text-[#7c6f9c] shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>
            </Card>
          </div>
        );
      case 'tips':
        return (
          <ul key={idx} className="space-y-2 my-4">
            {block.items.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-stone-600">
                <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        );
      case 'swaps':
        return (
          <div key={idx} className="space-y-3 my-6">
            {block.items.map((swap, i) => (
              <div key={i} className="grid md:grid-cols-2 gap-2 p-3 bg-stone-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-red-600 bg-red-50 px-2 py-0.5 rounded">Instead of</span>
                  <span className="text-sm text-stone-600 line-through">{swap.before}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-green-600 bg-green-50 px-2 py-0.5 rounded">Try</span>
                  <span className="text-sm text-stone-700 font-medium">{swap.after}</span>
                </div>
              </div>
            ))}
          </div>
        );
      case 'beforeafter':
        return (
          <div key={idx} className="grid md:grid-cols-2 gap-4 my-6">
            <Card className="p-5 rounded-xl border-red-200 bg-red-50/50">
              <p className="font-medium text-red-700 mb-3 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center text-xs">X</span>
                Before (Deficit-based)
              </p>
              <p className="text-sm text-stone-600 italic">{block.before}</p>
            </Card>
            <Card className="p-5 rounded-xl border-green-200 bg-green-50/50">
              <p className="font-medium text-green-700 mb-3 flex items-center gap-2">
                <CheckCircle className="w-5 h-5" />
                After (Affirming)
              </p>
              <p className="text-sm text-stone-700">{block.after}</p>
            </Card>
          </div>
        );
      case 'stagestrategy':
        return (
          <Card key={idx} className="p-6 my-6 rounded-xl border-stone-200">
            <div className="flex items-center gap-3 mb-4">
              <Badge className="bg-[#7c6f9c] text-white">{block.stage}</Badge>
              <span className="text-sm text-stone-500">Goal: {block.goal}</span>
            </div>
            <ul className="space-y-2 mb-4">
              {block.strategies.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-stone-600">
                  <Play className="w-4 h-4 text-[#7c9c8b] shrink-0 mt-0.5" />
                  {s}
                </li>
              ))}
            </ul>
            <div className="bg-[#f0f5f2] p-4 rounded-lg">
              <p className="text-xs font-medium text-[#7c9c8b] mb-1">Example</p>
              <p className="text-sm text-stone-700">{block.example}</p>
            </div>
          </Card>
        );
      case 'activities':
        return (
          <div key={idx} className="grid md:grid-cols-2 gap-3 my-6">
            {block.items.map((item, i) => (
              <Card key={i} className="p-4 rounded-xl border-stone-200">
                <p className="font-medium text-primary-900 mb-1">{item.interest}</p>
                <p className="text-sm text-stone-600">{item.activities}</p>
              </Card>
            ))}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 bg-[#FDFBF7]/90 backdrop-blur-sm z-50 border-b border-stone-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-primary" />
            <span className="font-serif text-primary-900">PlayFirst</span>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleShare}
            className="rounded-full"
          >
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
        </div>
      </nav>

      {/* Hero */}
      <header className={`pt-24 pb-12 px-4 bg-gradient-to-br ${article.heroColor}`}>
        <div className="max-w-3xl mx-auto text-center text-white">
          <Badge className="mb-4 bg-white/20 text-white border-white/30">
            <BookOpen className="w-3 h-3 mr-1" /> {article.category}
          </Badge>
          <h1 className="text-3xl md:text-4xl font-serif mb-4">
            {article.title}
          </h1>
          <div className="flex items-center justify-center gap-4 text-white/80 text-sm">
            <span>{article.author}</span>
            <span>•</span>
            <span>{article.publishDate}</span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" /> {article.readTime}
            </span>
          </div>
        </div>
      </header>

      {/* Article Content */}
      <article className="px-4 py-12">
        <div className="max-w-3xl mx-auto">
          {article.content.map((block, idx) => renderContent(block, idx))}
        </div>
      </article>

      {/* Related Articles */}
      <section className="px-4 py-12 bg-stone-50">
        <div className="max-w-3xl mx-auto">
          <h3 className="text-xl font-serif text-primary-900 mb-6">Continue Reading</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {article.relatedArticles.map((slug) => (
              <Card 
                key={slug}
                className="p-5 rounded-xl border-stone-200 hover:border-[#7c6f9c] hover:shadow-md transition-all cursor-pointer"
                onClick={() => navigate(`/resources/${slug}`)}
              >
                <p className="font-serif text-primary-900 mb-2">{ARTICLE_TITLES[slug]}</p>
                <span className="text-sm text-[#7c6f9c] flex items-center gap-1">
                  Read article <ChevronRight className="w-4 h-4" />
                </span>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-12">
        <div className="max-w-3xl mx-auto text-center">
          <Card className="p-8 rounded-2xl bg-gradient-to-br from-[#f5f3f8] to-stone-50 border-0">
            <Heart className="w-10 h-10 text-[#7c6f9c] mx-auto mb-4" />
            <h3 className="text-xl font-serif text-primary-900 mb-2">
              Ready to try PlayFirst?
            </h3>
            <p className="text-stone-600 mb-6">
              AI-powered reports and documentation built for neurodiversity-affirming therapists.
            </p>
            <Button 
              onClick={() => navigate('/')}
              className="rounded-full"
            >
              Get Started Free
            </Button>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-stone-100">
        <div className="max-w-4xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <Heart className="w-5 h-5 text-primary" />
            <span className="font-serif text-primary-900">PlayFirst</span>
          </div>
          <p className="text-sm text-stone-500">
            © {new Date().getFullYear()} PlayFirst. Built for speech therapists with love.
          </p>
        </div>
      </footer>
    </div>
  );
}
